import React, { useEffect, useState } from 'react';
import { borderRadius } from 'react-select/src/theme';
import styled, { css } from 'styled-components';
import {
    Badge,
    Box,
    Button,
    IconButton,
    Inline,
    Menu,
    MenuDivider,
    MenuItem,
    Overlay,
    Spinner,
    Text,
    createButtonPrimaryColorConfig,
} from '@99designs/emu';
import { boxShadows, colors, spaces } from '@99designs/emu/settings';
import { IconExperiment1, IconX1 } from '@99designs/icons';
import { JaegerMenuProps } from '../JaegerMenu/JaegerMenu';
import { useJaeger } from '../useJaeger/useJaeger';

interface DebugBarProps {
    visible?: boolean;
    service?: string;
}

// Create a styled button that is floating and fixed in the bottom right corner
// of the screen.
const DebugButtonContainer = styled(Box)`
    z-index: 1000;
    padding: ${spaces['1bu']} ${spaces['1.5bu']};
`;

const VisibilityToggleContainer = styled(Box)`
    transition: transform 100ms;
    transform: translate(20px, 20px);

    &:hover {
        transform: translate(0, 0);
    }
`;

const JaegerMenu = React.lazy(() => import('../JaegerMenu/JaegerMenu'));

const DEFAULT_ERROR_TIMEFRAME = 120;

export function DebugBar({ visible, service }: DebugBarProps) {
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [barVisible, setBarVisible] = useState(visible);
    const [modalProps, setModalProps] = useState<JaegerMenuProps>({
        mode: 'search',
        service: 'bastion',
        errorsOnly: false,
    });

    const traces = useJaeger({ timeframe: DEFAULT_ERROR_TIMEFRAME, service: service || 'bastion' });

    const setJaegerModal = (options: Partial<JaegerMenuProps>) => {
        setModalProps((prev) => ({ ...prev, ...options }));
        setOverlayVisible(!overlayVisible);
    };

    const style = {
        boxShadow: boxShadows.high,
    };

    return (
        <>
            <DebugButtonContainer
                display={barVisible ? 'block' : 'none'}
                margin="1bu"
                bottom="none"
                right="none"
                position="fixed"
            >
                <Inline noWrap space="0.25bu">
                    <Menu
                        disclosure={
                            <Button style={style}>
                                <Inline space="1bu">
                                    <Text
                                        color={
                                            traces.length > 0 ? colors.semantic.error : colors.white
                                        }
                                        weight="bold"
                                    >
                                        Debug
                                    </Text>
                                    {traces.length > 0 && (
                                        <Badge value={traces.length} size="regular" />
                                    )}
                                </Inline>
                            </Button>
                        }
                    >
                        <MenuItem
                            onClick={() => setJaegerModal({ mode: 'search', errorsOnly: false })}
                        >
                            View Traces
                        </MenuItem>
                        <MenuItem
                            onClick={() => setJaegerModal({ mode: 'search', errorsOnly: true })}
                        >
                            View Errors {!!traces.length && `(${traces.length})`}
                        </MenuItem>
                        <MenuDivider />
                        {traces.length > 0 && (
                            <>
                                {traces.map((trace) => (
                                    <MenuItem
                                        onClick={() =>
                                            setJaegerModal({
                                                mode: 'trace',
                                                traceId: trace?.id,
                                            })
                                        }
                                        key={trace?.id}
                                    >
                                        <Inline space="0.25bu">
                                            <Text size="size2" color={colors.semantic.error}>
                                                {trace?.title}
                                            </Text>
                                            <Text size="size2" color={colors.grey400}>
                                                {trace?.id}
                                            </Text>
                                        </Inline>
                                    </MenuItem>
                                ))}
                            </>
                        )}
                    </Menu>
                    <IconButton
                        aria-label="Hide Debug Bar"
                        icon={IconX1}
                        onClick={() => {
                            return setBarVisible(!barVisible);
                        }}
                    />
                </Inline>
            </DebugButtonContainer>
            <VisibilityToggleContainer
                display={barVisible ? 'none' : 'block'}
                position="fixed"
                bottom="none"
                right="none"
            >
                <IconButton
                    aria-label="Toggle Debug Bar"
                    onClick={() => setBarVisible(!barVisible)}
                    icon={IconExperiment1}
                    appearance={traces.length > 0 ? ErrorButtonAppearance : 'primary'}
                />
            </VisibilityToggleContainer>

            <Overlay
                visible={overlayVisible}
                onClose={() => setOverlayVisible(false)}
                aria-label="Debug Bar"
            >
                <React.Suspense fallback={<Spinner size="large" />}>
                    <JaegerMenu {...modalProps} />
                </React.Suspense>
            </Overlay>
        </>
    );
}

const ErrorButtonAppearance = createButtonPrimaryColorConfig({
    color: colors.semantic.error,
    backgroundColorActive: colors.experimental.red600,
    backgroundColorHover: colors.experimental.red300,
});

export default DebugBar;
